import { isDueToday, isRunningOut, progressOf } from './leadLoop';
import type { LeadGroup } from './leadGrouping';
import type { RunningRound } from './leadRounds';
import type { Lead } from './types';

/**
 * The list as a follow-up work queue: leads grouped by the round they are in.
 *
 * Kyu: "when you click rounds, we should be able to see who's in round one,
 * round two, round three, round four... and then last touch, next email
 * scheduled on what date. It goes back to what Alex wants to see." The rounds
 * machinery existed and was invisible unless you opened the Filters panel and
 * knew what "In a round" meant — the mechanism for Alex's "clearly worked"
 * requirement, hidden behind a disclosure.
 *
 * ── A view, not a filter ───────────────────────────────────────────────────
 *
 * Same contract as Group by company: it changes how the current list is
 * DRAWN, and composes with the tab, the search and every chip. "Inbound,
 * Corporate Gifting, in rounds" is the tab plus a chip plus this — no
 * special cases, and the counts everywhere else stay true.
 *
 * ── Only leads currently IN a round ────────────────────────────────────────
 *
 * That is what makes it a work queue. "Needs a next round" and "never in a
 * round" are different questions and stay where they are, as loop chips in
 * the Filters panel. A queue that also listed everyone outside the loop
 * would be the whole list again, wearing headers.
 *
 * ── Round numbers appear as they come into existence ───────────────────────
 *
 * No empty "Round 3" header waiting for a future that may not come. On the
 * day this shipped there was exactly one group to show, because the first
 * real round started that morning.
 */

/** Rendered as LeadGroup so the table draws it with the machinery it has. */
export function groupLeadsByRound(
  leads: Lead[],
  running: ReadonlyMap<string, RunningRound>,
  todayIso: string,
): LeadGroup[] {
  const byNumber = new Map<number, Lead[]>();
  for (const lead of leads) {
    const round = running.get(lead.id);
    if (round === undefined) {
      continue;
    }
    const held = byNumber.get(round.number);
    if (held === undefined) {
      byNumber.set(round.number, [lead]);
    } else {
      held.push(lead);
    }
  }

  return [...byNumber.entries()]
    .sort(([a], [b]) => a - b)
    .map(([number, members]) => {
      // WORST FIRST, because the queue exists to be worked from the top:
      // running out, then due today, then on pace. `sort` is stable, so leads in
      // the same state keep the list's existing order.
      const ranked = [...members].sort(
        (a, b) => urgencyRank(a, running, todayIso) - urgencyRank(b, running, todayIso),
      );
      const worked = ranked.filter((lead) => lead.touch_count > 0).length;
      return {
        key: `round-${String(number)}`,
        kind: 'round' as const,
        companyId: null,
        companyName: `Round ${String(number)}`,
        leads: ranked,
        worked,
        untouched: ranked.length - worked,
      };
    });
}

/** running out = 0, due today = 1, on pace = 2. Running out IMPLIES due, so
 *  it is checked first, or every running-out lead would be filed under due. */
function urgencyRank(
  lead: Lead,
  running: ReadonlyMap<string, RunningRound>,
  todayIso: string,
): number {
  const progress = progressOf(running.get(lead.id), todayIso);
  if (isRunningOut(progress)) {
    return 0;
  }
  if (isDueToday(progress)) {
    return 1;
  }
  return 2;
}

/**
 * What the header says about the leads under it.
 *
 * Running out and due today are DISJOINT here — a running-out lead is not also
 * counted as due, or "3 leads · 3 due today · 2 running out" would describe five states
 * across three people and read as a miscount.
 */
export function roundGroupSummary(
  group: LeadGroup,
  running: ReadonlyMap<string, RunningRound>,
  todayIso: string,
): string {
  const people = `${String(group.leads.length)} ${group.leads.length === 1 ? 'lead' : 'leads'}`;
  let runningOut = 0;
  let due = 0;
  for (const lead of group.leads) {
    const rank = urgencyRank(lead, running, todayIso);
    if (rank === 0) {
      runningOut += 1;
    } else if (rank === 1) {
      due += 1;
    }
  }
  if (runningOut === 0 && due === 0) {
    return `${people} · all on pace`;
  }
  const parts = [people];
  if (runningOut > 0) {
    parts.push(`${String(runningOut)} running out of days`);
  }
  if (due > 0) {
    parts.push(`${String(due)} due today`);
  }
  return parts.join(' · ');
}

// ── Remembering the toggle ─────────────────────────────────────────────────
//
// Same shape as Group by company, and the same reasoning: off unless this
// person turned it on, remembered per browser, and guarded because a browser
// set to block site data throws on ACCESS — a leads table that will not
// render because it could not remember a toggle would be the worse bug.

const STORAGE_KEY = 'tap.leads.roundsView';

export function readRoundsView(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function writeRoundsView(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // The toggle works either way; only the memory of it is lost.
  }
}
